import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@carrent/ui'
import { api, tasksApi } from '@carrent/api'
import { statusMeta, priorityMeta } from '@carrent/domain'
import { formatAgo } from './CarChangeList'

/**
 * Активность пользователя: что он менял и какие задачи на нём висят.
 *
 * ── Откуда данные ─────────────────────────────────────────────────────
 *
 * Действия — из журнала аудита, суженного до одного actor_id. Это тот же
 * журнал, что питает историю машины: отдельного хранилища активности
 * нет и не нужно, иначе два списка разошлись бы.
 *
 * Задачи — существующий `GET /api/tasks?assignee=<id>`: фильтр по
 * исполнителю в нём был всегда, страница просто впервые им пользуется.
 *
 * ── Что показывает строка действия ────────────────────────────────────
 *
 * «Что сделал → с чем → когда»: car.updated превращается в «Изменил
 * машину», ссылка ведёт на объект. Разбор before/after здесь не
 * показывается — это уровень истории объекта, а не ленты человека:
 * лента отвечает «чем он занимался», а не «что именно стало с полем».
 */

// Русские заголовки действий. Не каждое действие из реестра — только те,
// что осмысленны в ленте человека; остальные показываются техническим
// именем, а не пропадают: скрытая активность хуже некрасивой.
const ACTION_TITLES = {
  'car.created': 'Завёл машину',
  'car.updated': 'Изменил машину',
  'car.status_changed': 'Сменил статус машины',
  'car.photo_changed': 'Обновил изображение машины',
  'car.document_added': 'Добавил документ машины',
  'car.document_deleted': 'Удалил документ машины',
  'car.transferred': 'Передал машину в другой регион',
  'car.damage_registered': 'Зарегистрировал повреждение',
  'car.damage_repaired': 'Отметил повреждение устранённым',
  'user.updated': 'Изменил пользователя',
  'user.role_changed': 'Сменил роль пользователя',
  'user.status_changed': 'Сменил статус пользователя',
  'trip.confirmed': 'Подтвердил поездку',
  'task.created': 'Создал задачу',
  'task.updated': 'Изменил задачу',
  'task.status_changed': 'Сменил статус задачи',
  'kb.page_created': 'Создал страницу базы знаний',
  'kb.page_updated': 'Изменил страницу базы знаний',
  'kb.page_reverted': 'Откатил страницу базы знаний',
}

const ENTITY_ROUTES = {
  car: (id) => `/admin/cars/${id}`,
  user: (id) => `/admin/users/${id}`,
  trip: (id) => `/admin/trips/${id}`,
  task: (id) => `/tasks/${id}`,
}

export function UserActivity({ userId }) {
  const [entries, setEntries] = useState([])
  const [meta, setMeta] = useState({ page: 1, pages: 1 })
  const [page, setPage] = useState(1)
  const [state, setState] = useState('loading') // loading | ready | error | forbidden

  const load = useCallback(async () => {
    setState('loading')
    try {
      const r = await api.get('/admin/audit', {
        params: { actor_id: userId, page },
      })
      setEntries(r.data?.data?.items || [])
      setMeta(r.data?.data?.meta || { page: 1, pages: 1 })
      setState('ready')
    } catch (e) {
      // Журнал аудита доступен только админу. Менеджер видит карточку
      // пользователя, но не его действия — и это правило сервера,
      // а не поломка страницы.
      setState(e?.response?.status === 403 ? 'forbidden' : 'error')
    }
  }, [userId, page])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  if (state === 'forbidden') return null

  if (state === 'loading') {
    return (
      <div className="flex-col gap-sm">
        {Array.from({ length: 3 }, (_, i) => (
          <span key={i} className="skeleton table__skeleton-row" />
        ))}
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="state">
        <p className="state__title">Не удалось загрузить активность</p>
        <button type="button" className="btn btn--secondary" onClick={load}>
          Повторить
        </button>
      </div>
    )
  }

  if (entries.length === 0) {
    return <p className="activity__empty">Действий пока не записано.</p>
  }

  return (
    <div className="activity">
      <ol className="activity__list">
        {entries.map((entry) => (
          <ActivityRow key={entry.id} entry={entry} />
        ))}
      </ol>

      {meta.pages > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--ghost btn--sm"
            disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <Icon name="chevronLeft" size={14} />
            Новее
          </button>
          <span className="pager__position">Страница {page} из {meta.pages}</span>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled={page >= meta.pages} onClick={() => setPage(page + 1)}>
            Старее
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

function ActivityRow({ entry }) {
  const title = ACTION_TITLES[entry.action] || entry.action
  const route = ENTITY_ROUTES[entry.entity_type]
  const when = new Date(entry.created_at)
  // Сколько полей затронуто — единственная деталь из содержимого:
  // достаточно, чтобы отличить правку одного поля от массовой.
  const touched = Object.keys(entry.after || {}).length

  return (
    <li className="activity__row">
      <span className="activity__title">
        {route && entry.entity_id ? (
          <Link to={route(entry.entity_id)}>{title}</Link>
        ) : (
          title
        )}
        {touched > 1 && entry.action.endsWith('.updated') && (
          <span className="activity__detail"> · полей: {touched}</span>
        )}
      </span>
      <time className="activity__time" dateTime={entry.created_at}
        title={when.toLocaleString('ru', { dateStyle: 'full', timeStyle: 'medium' })}>
        {formatAgo(when)}
      </time>
    </li>
  )
}

/**
 * Задачи на пользователе. Открытые сверху, решённые не показываются:
 * вопрос секции — «что на нём висит», а не «что он когда-либо делал».
 */
export function UserTasks({ userId }) {
  const [tasks, setTasks] = useState(null)

  useEffect(() => {
    let alive = true
    tasksApi
      .list({ assignee: userId, per_page: 20 })
      .then((r) => {
        if (!alive) return
        const items = (r.data?.data?.items || []).filter(
          (t) => !['done', 'cancelled'].includes(t.status),
        )
        setTasks(items)
      })
      .catch(() => alive && setTasks([]))
    return () => {
      alive = false
    }
  }, [userId])

  if (tasks === null) {
    return <span className="skeleton table__skeleton-row" />
  }

  if (tasks.length === 0) {
    return <p className="activity__empty">Открытых задач нет.</p>
  }

  return (
    <ol className="activity__list">
      {tasks.map((task) => {
        const st = statusMeta(task.status)
        const pr = priorityMeta(task.priority)
        return (
          <li key={task.id} className="activity__row">
            <span className="activity__title">
              <Link to={`/tasks/${task.id}`}>{task.title}</Link>
              <span className={`badge badge--${pr.tone} activity__badge`}>{pr.label}</span>
            </span>
            <span className="activity__time">{st.label}</span>
          </li>
        )
      })}
    </ol>
  )
}
